// /campaigns — dashboard page. Composes stats, filters, and the list.
// Header is rendered inline in the first PageSection (not in `staticElements`),
// so it scrolls with the content rather than being pinned.

import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { Page, Typo, SimpleButton, Flex, PageHeader, PageSection } from '../../ui';
import CampaignStatsGrid from './components/dashboard/CampaignStatsGrid';
import CampaignsFilterBar from './components/dashboard/CampaignsFilterBar';
import CampaignsList from './components/dashboard/CampaignsList';
import NewCampaignButton from './components/dashboard/NewCampaignButton';
import { useCampaignsList } from './hooks/useCampaignsList';
import { useCampaignPermissions } from './hooks/useCampaignPermissions';
import { useCampaignsActions } from './hooks/useCampaignsActions';

const FeatureDisabled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
  padding: 40px;
  gap: 8px;
`;

const Campaigns = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = useCampaignPermissions();
  const { items, filters, isLoading, updateFilters, clearFilters } = useCampaignsList();
  const { exportAudience } = useCampaignsActions();

  const handleCreate = useCallback(() => navigate('/campaigns/new'), [navigate]);
  const handleExport = useCallback(() => {
    exportAudience({ filters, format: 'csv' }).catch(() => {});
  }, [exportAudience, filters]);

  if (!permissions.featureEnabled) {
    return (
      <Page>
        <PageSection>
          <FeatureDisabled>
            <Typo.ContentSmallHeader>{t('campaigns.featureDisabled', 'Campaigns are not enabled')}</Typo.ContentSmallHeader>
            <Typo.MediumText color='textSecondary'>
              {t('campaigns.enableInSettings', 'Ask an admin to enable the campaigns feature for this store.')}
            </Typo.MediumText>
          </FeatureDisabled>
        </PageSection>
      </Page>
    );
  }

  return (
    <Page gap={24}>
      <PageSection gap={20} align='stretch'>
        <PageHeader>
          <Flex direction='column' align='stretch'>
            <Typo.PageHeader $textAlign='left'>{t('campaigns.title', 'Campaigns')}</Typo.PageHeader>
            <Typo.SmallText color='textSecondary'>
              {t('campaigns.pageDescription', 'Plan, send and measure student outreach across email, invitations and discounts.')}
            </Typo.SmallText>
          </Flex>
          <Flex gap='12'>
            {permissions.canExportAudience && items.length > 0 && (
              <SimpleButton variant='tertiary' startIcon='Download' onClick={handleExport}>
                {t('globals.export', 'Export')}
              </SimpleButton>
            )}
            <NewCampaignButton />
          </Flex>
        </PageHeader>
        <CampaignStatsGrid />
        <CampaignsFilterBar value={filters} onChange={updateFilters} />
        <CampaignsList items={items} filters={filters} isLoading={isLoading} onClearFilters={clearFilters} onCreate={handleCreate} />
      </PageSection>
    </Page>
  );
};

export default Campaigns;
